/**
 * Generic crafting calculator (`/crafting`, inbox #307): pure logic over the
 * database recipe contract (data-forge add-game `references/database-quality.md`
 * §5.1), shared by the server pages and the client calculator.
 *
 * Contract, in short:
 * - a recipe is any entry with `ingredients: DbRef[]` (`count` = amount, default 1);
 * - its output is `products: DbRef[]` (`count` = yield per craft, default 1);
 *   an entry WITHOUT `products` is the recipe for itself, yield 1;
 * - when explicit recipes (with `products`) make an item, the item's own
 *   `ingredients` are a mirror of them and ignored;
 * - stations come as `craftedIn` / `craftedAt` / `producedIn` DbRefs, or the
 *   display strings `craftable.station` / `Station`;
 * - ingredient refs that share a `group` are ONE "any of" slot (Palia "Any
 *   Fish"): any member satisfies it, the first member is the default and the
 *   player swaps it with `choice[slotKey(group)]`.
 *
 * Everything here is side-effect free (no fetch, no DOM) so it runs and is
 * tested on both sides. The XP planner (#305) reuses `planCrafting` for the
 * material cost of a training method.
 */

/**
 * A stack of one entry: `count` of `id` (an entry of db section `section`).
 * `any` = an "any of" ingredient slot: `id` is its default member and
 * `options` every member (default first).
 */
export type CraftRef = {
  id: string;
  section: string;
  count: number;
  any?: { group: string; options: { id: string; section: string }[] };
};

/** Where a recipe is made: a db entry (linkable) or a display label. */
export type CraftStation =
  | { id: string; section: string; label?: undefined }
  | { id?: undefined; section?: undefined; label: string };

export type CraftRecipe = {
  /** Id of the db entry that carries the recipe (a recipe or the item itself). */
  key: string;
  /** Db section of that entry. */
  section: string;
  products: CraftRef[];
  ingredients: CraftRef[];
  stations: CraftStation[];
  /** Marked `Alternate` in the data (Satisfactory) — never the default. */
  alternate: boolean;
  /** Derived from an item's own `ingredients` (no `products` prop). */
  implicit: boolean;
  /**
   * Ingredient ids the craft starts with (`startWith` prop, Palia multi-step
   * dishes); the other ingredients are added afterwards to finish it.
   */
  start?: string[];
  /** What unlocks the recipe (`RECIPE_GATE_PROPS`), shown next to the station. */
  gates?: CraftGate[];
};

/** One unlock requirement of a recipe: the prop and its value. */
export type CraftGate = { prop: string; value: string | number };

/**
 * Props that gate a recipe — what the player needs before they can use it
 * (AION 2 "Mastery Level" 25 vs 1 for the 10× and the 1× Magic Crystal,
 * #2656). A positive number or a non-empty text counts.
 */
export const RECIPE_GATE_PROPS = [
  "Mastery Level",
  "Required Mastery",
  "Required Mastery Level",
  "Required Skill Level",
  "Required Level",
  "Monolith Level",
  "Unlock Level",
  "Unlocked At",
  "Unlock Planet",
  "Unlock",
  "unlock",
  "requiredBuildingMastery",
  "requiredSmithingMastery",
  "requiredFarmingMastery",
  "requiredBookWritingMastery",
  "requiredAnatomyMastery",
] as const;

/**
 * Gate props whose text already names the requirement ("Alchemy Lv. 20",
 * "Unlocked at Courier Rank 5"): shown without the prop label.
 */
export const GATE_VALUE_ONLY: ReadonlySet<string> = new Set([
  "Required Mastery",
  "Required Skill Level",
  "Unlock",
  "unlock",
]);

/**
 * Only on a recipe entry: on an item that is its own recipe, "Required Level"
 * is the level to equip or use the item (Souls' Remnant), not to craft it.
 */
const RECIPE_ONLY_GATES: ReadonlySet<string> = new Set(["Required Level"]);

/** The gate props of one entry's props. */
export function readGates(
  props: Record<string, unknown>,
  implicit = false,
): CraftGate[] {
  const out: CraftGate[] = [];
  for (const prop of RECIPE_GATE_PROPS) {
    if (implicit && RECIPE_ONLY_GATES.has(prop)) continue;
    const value = props[prop];
    if (
      (typeof value === "number" && Number.isFinite(value) && value > 0) ||
      (typeof value === "string" && value.trim())
    )
      out.push({ prop, value });
  }
  return out;
}

export type CraftingGraph = {
  recipes: CraftRecipe[];
  /** Item id → indices into `recipes` that make it, best first. */
  byProduct: Record<string, number[]>;
  /** Item id → the recipe used by default; null = gathered by default. */
  defaults: Record<string, number | null>;
  /**
   * Items bought or gathered by default when they are an INGREDIENT (`_buy`
   * prop: Elderfield Coal, sold by the Forge Shop, also has Furnace
   * recipes); as the item asked for they are still crafted (`defaults`).
   */
  bought: Record<string, true>;
  /** Item id → indices into `recipes` that consume it. */
  usedIn: Record<string, number[]>;
  /** Every id seen (product, ingredient, station) → its db section. */
  sectionOf: Record<string, string>;
  /** "Any of" slot group → its member ids (default first). */
  slots: Record<string, string[]>;
};

/** Minimal db shapes this module reads (structurally `DatabaseConfig`). */
export type CraftDbEntry = { id: string; props?: Record<string, unknown> };
export type CraftDbCategory = { type: string; items: CraftDbEntry[] };

type RawRef = {
  id?: unknown;
  section?: unknown;
  count?: unknown;
  group?: unknown;
};

/** Choice key of an "any of" slot (item ids never start with `~`). */
export function slotKey(group: string): string {
  return `~${group}`;
}

/** The item an ingredient resolves to under `choice` (slot pick or itself). */
export function ingredientId(g: CraftRef, choice: RecipeChoice = {}): string {
  if (!g.any) return g.id;
  const pick = choice[slotKey(g.any.group)];
  return pick && g.any.options.some((o) => o.id === pick) ? pick : g.id;
}

function toRefs(
  value: unknown,
  fallbackSection?: string,
  /** Merge refs sharing a `group` into one "any of" slot (ingredients). */
  slots = false,
): CraftRef[] {
  const list = Array.isArray(value)
    ? value
    : value && typeof value === "object" && "list" in value
      ? (value as { list: unknown }).list
      : value && typeof value === "object" && "id" in value
        ? [value]
        : [];
  if (!Array.isArray(list)) return [];
  const out: CraftRef[] = [];
  for (const raw of list as RawRef[]) {
    if (!raw || typeof raw.id !== "string" || !raw.id) continue;
    const section =
      typeof raw.section === "string" && raw.section
        ? raw.section
        : fallbackSection;
    if (!section) continue;
    const n = typeof raw.count === "number" ? raw.count : Number(raw.count);
    const count = Number.isFinite(n) && n > 0 ? n : 1;
    if (slots && typeof raw.group === "string" && raw.group) {
      const group = raw.group;
      const slot = out.find((r) => r.any?.group === group);
      if (slot) {
        if (!slot.any!.options.some((o) => o.id === raw.id))
          slot.any!.options.push({ id: raw.id, section });
      } else {
        out.push({
          id: raw.id,
          section,
          count,
          any: { group, options: [{ id: raw.id, section }] },
        });
      }
      continue;
    }
    // The same ingredient listed twice (rare data quirk) = one stack.
    const prev = out.find((r) => r.id === raw.id && !r.any);
    if (prev) prev.count += count;
    else out.push({ id: raw.id, section, count });
  }
  return out;
}

const STATION_REF_KEYS = ["craftedIn", "craftedAt", "producedIn"] as const;

/** Normalize the station shapes of one entry's props. */
export function readStations(props: Record<string, unknown>): CraftStation[] {
  const refs = STATION_REF_KEYS.flatMap((k) => toRefs(props[k]));
  if (refs.length) {
    const seen = new Set<string>();
    return refs
      .filter((r) => !seen.has(r.id) && seen.add(r.id))
      .map((r) => ({ id: r.id, section: r.section }));
  }
  const craftable = props.craftable as { station?: unknown } | undefined;
  const label =
    (craftable && typeof craftable.station === "string" && craftable.station) ||
    (typeof props.Station === "string" && props.Station) ||
    "";
  return label ? [{ label }] : [];
}

function isAlternate(props: Record<string, unknown>): boolean {
  const v = props.Alternate ?? props.alternate;
  return v === true || (typeof v === "string" && /^(yes|true)$/i.test(v));
}

const PURCHASE = /shop|vendor|store|merchant|trader/i;

/**
 * A "recipe" that buys the item (Witchspire shop recipes) or upgrades the
 * previous tier (Starsand `_upgrade` formulas) — a valid alternative, but not
 * what "how do I craft X" means, so never the default when another exists.
 */
function isPurchaseOrUpgrade(r: CraftRecipe): boolean {
  return (
    /(^|_)(upgrade|shop)(_|$)/i.test(r.key) ||
    r.stations.some((s) => PURCHASE.test(s.label ?? s.id ?? ""))
  );
}

/** `recipe_fuel` / `item_fuel` → `fuel` (the shared name stem). */
function stem(id: string): string {
  const i = id.indexOf("_");
  return (i >= 0 ? id.slice(i + 1) : id).toLowerCase();
}

/**
 * Build the recipe graph from database categories (full type files — the
 * index alone carries no props). Order of `byProduct[id]` = default first:
 * standard over alternate, main output over byproduct, a recipe named after
 * the item, not consuming its own product, then data order.
 */
export function buildCraftingGraph(
  categories: CraftDbCategory[],
  options: {
    /** Gathered by default even when craftable (adds to `_gather` props). */
    gatherable?: Iterable<string>;
  } = {},
): CraftingGraph {
  const recipes: CraftRecipe[] = [];
  const sectionOf: Record<string, string> = {};
  const implicit: CraftRecipe[] = [];
  for (const cat of categories) {
    // In a recipe section (entries name OTHER entries as products), an entry
    // without `products` is an incomplete recipe — never "makes itself".
    // (An item's self-ref yield does not make its section a recipe section.)
    const recipeSection = cat.items.some((e) =>
      toRefs(e.props?.products, cat.type).some((p) => p.id !== e.id),
    );
    for (const entry of cat.items) {
      sectionOf[entry.id] ??= cat.type;
      const props = entry.props;
      if (!props) continue;
      const ingredients = toRefs(props.ingredients, cat.type, true);
      if (!ingredients.length) continue;
      const products = toRefs(props.products, cat.type);
      if (!products.length && recipeSection) continue;
      const start = toRefs(props.startWith, cat.type).map((r) => r.id);
      const gates = readGates(props, !products.length);
      const recipe: CraftRecipe = {
        key: entry.id,
        section: cat.type,
        products: products.length
          ? products
          : [{ id: entry.id, section: cat.type, count: 1 }],
        ingredients,
        stations: readStations(props),
        alternate: isAlternate(props),
        implicit: !products.length,
        ...(start.length ? { start } : {}),
        ...(gates.length ? { gates } : {}),
      };
      (products.length ? recipes : implicit).push(recipe);
    }
  }
  // Explicit recipes win: an item's own ingredients mirror them — unless all
  // of them are alternates (Grounded 2 lists only the extra ways to make an
  // item in its recipe section; the item keeps its standard recipe). Only a
  // recipe with the item as its MAIN output mirrors it: a by-product (Conan
  // Exiles dye mixing hands back the Glass Flask) does not (#2044).
  const explicitProducts = new Set(
    recipes.flatMap((r) => (r.alternate ? [] : [r.products[0].id])),
  );
  for (const r of implicit) {
    if (!explicitProducts.has(r.products[0].id)) recipes.push(r);
  }
  // An item's own gate (Palworld "Unlock Level" on the item, its recipe a
  // separate entry) gates every recipe making it that names none (#2656).
  const itemGates = new Map(
    implicit.flatMap((r): [string, CraftGate[]][] =>
      r.gates ? [[r.products[0].id, r.gates]] : [],
    ),
  );
  for (const r of recipes) {
    const gates = !r.implicit && !r.gates && itemGates.get(r.products[0].id);
    if (gates) r.gates = gates;
  }

  const byProduct: Record<string, number[]> = {};
  const usedIn: Record<string, number[]> = {};
  const slots: Record<string, string[]> = {};
  recipes.forEach((r, i) => {
    for (const p of r.products) {
      sectionOf[p.id] ??= p.section;
      (byProduct[p.id] ??= []).push(i);
    }
    for (const ing of r.ingredients) {
      // A slot is "used in" for every member.
      const members = ing.any?.options ?? [ing];
      for (const m of members) {
        sectionOf[m.id] ??= m.section;
        const list = (usedIn[m.id] ??= []);
        if (list[list.length - 1] !== i) list.push(i);
      }
      if (ing.any) {
        const all = (slots[ing.any.group] ??= []);
        for (const m of ing.any.options)
          if (!all.includes(m.id)) all.push(m.id);
      }
    }
    for (const s of r.stations) if (s.id) sectionOf[s.id] ??= s.section;
  });
  // Two recipes that turn A into B and B back into A (Palworld Pal Souls,
  // Satisfactory packaging): the direction that makes MORE units than it
  // consumes is the breakdown — never the default.
  const reversesLoop = (r: CraftRecipe, id: string) => {
    const made = r.products.reduce((n, p) => n + p.count, 0);
    const used = r.ingredients.reduce((n, g) => n + g.count, 0);
    if (made <= used) return false;
    return r.ingredients.some((g) =>
      (byProduct[g.id] ?? []).some((j) =>
        recipes[j].ingredients.some((h) => h.id === id),
      ),
    );
  };
  const gatherable = new Set(options.gatherable ?? []);
  const bought: Record<string, true> = {};
  for (const cat of categories) {
    for (const entry of cat.items) {
      if (entry.props?._gather === true) gatherable.add(entry.id);
      if (entry.props?._buy === true && byProduct[entry.id])
        bought[entry.id] = true;
    }
  }
  const defaults: Record<string, number | null> = {};
  for (const [id, list] of Object.entries(byProduct)) {
    const score = (i: number) => {
      const r = recipes[i];
      return (
        (r.products[0].id === id ? 0 : 16) +
        (r.alternate ? 8 : 0) +
        (isPurchaseOrUpgrade(r) ? 4 : 0) +
        (stem(r.key) === stem(id) || r.key === id ? 0 : 2) +
        (r.ingredients.some((g) => g.id === id) ? 1 : 0)
      );
    };
    list.sort((a, b) => score(a) - score(b) || a - b);
    // Default: gather what the data flags `_gather` (Satisfactory Coal has
    // converter recipes, players mine it); else the best recipe that makes
    // the item as its MAIN output, does not consume it and is not a reverse
    // recipe (unpackage / breakdown). Nothing left → gathered.
    defaults[id] = gatherable.has(id)
      ? null
      : (list.find((i) => {
          const r = recipes[i];
          return (
            r.products[0].id === id &&
            !r.ingredients.some((g) => g.id === id) &&
            !REVERSE.test(r.key) &&
            !reversesLoop(r, id)
          );
        }) ?? null);
  }
  return { recipes, byProduct, defaults, bought, usedIn, sectionOf, slots };
}

/** Recipes that take an item apart again — never a default. */
const REVERSE =
  /(^|_)(un_?package\w*|unpack\w*|breakdown|break_down|recycl\w*|salvag\w*|dismantl\w*|disassembl\w*|decompos\w*)(_|$)/i;

/** `choice[id] = RAW_CHOICE`: gather / already have it, do not craft. */
export const RAW_CHOICE = "-";

/** Item ids crafted by default (a recipe page / picker entry each). */
export function craftableIds(graph: CraftingGraph): string[] {
  return Object.keys(graph.byProduct).filter(
    (id) => graph.defaults[id] != null,
  );
}

/** Recipe indices for an item, best first (empty = raw material). */
export function recipesFor(graph: CraftingGraph, id: string): number[] {
  return graph.byProduct[id] ?? [];
}

/**
 * Item id → chosen recipe KEY (`CraftRecipe.key`) or `RAW_CHOICE`;
 * missing = the graph default.
 */
export type RecipeChoice = Record<string, string>;

/**
 * The recipe index used for `id` under `choice` (undefined = raw). `root` =
 * `id` is the item asked for, not an ingredient (a `bought` item is only
 * crafted by default then).
 */
export function chosenRecipe(
  graph: CraftingGraph,
  id: string,
  choice: RecipeChoice = {},
  root = true,
): number | undefined {
  const list = graph.byProduct[id];
  if (!list?.length) return undefined;
  const key = choice[id];
  if (key === RAW_CHOICE) return undefined;
  if (key) {
    const hit = list.find((i) => graph.recipes[i].key === key);
    if (hit !== undefined) return hit;
  }
  if (!root && graph.bought?.[id]) return undefined;
  return graph.defaults[id] ?? undefined;
}

/** How many `id` one craft of `recipe` yields. */
export function recipeYield(recipe: CraftRecipe, id: string): number {
  return recipe.products.find((p) => p.id === id)?.count ?? 1;
}

export type CraftStep = {
  id: string;
  section: string;
  /** Amount needed. */
  qty: number;
  /** Recipe index used, undefined for a raw material. */
  recipe?: number;
  /** Times the recipe runs: ceil(qty / yield). */
  crafts: number;
  /** Amount one craft makes. */
  yield: number;
  /** Inputs for `crafts` runs (`group` = an "any of" slot, resolved). */
  children: { id: string; section: string; qty: number; group?: string }[];
};

/**
 * One level of the tree: what making `qty` of `id` takes (`root` = `id` is
 * the tree's top, see `chosenRecipe`).
 */
export function craftStep(
  graph: CraftingGraph,
  id: string,
  qty: number,
  choice: RecipeChoice = {},
  root = true,
): CraftStep {
  const section = graph.sectionOf[id] ?? "";
  const ri = chosenRecipe(graph, id, choice, root);
  if (ri === undefined) {
    return { id, section, qty, crafts: 0, yield: 1, children: [] };
  }
  const recipe = graph.recipes[ri];
  const y = recipeYield(recipe, id);
  const crafts = Math.ceil(qty / y);
  return {
    id,
    section,
    qty,
    recipe: ri,
    crafts,
    yield: y,
    children: recipe.ingredients.map((g) => {
      const use = ingredientId(g, choice);
      return {
        id: use,
        section: graph.sectionOf[use] ?? g.section,
        qty: g.count * crafts,
        ...(g.any ? { group: g.any.group } : {}),
      };
    }),
  };
}

export type CraftTarget = { id: string; qty: number };

export type CraftPlanLine = { id: string; section: string; qty: number };
export type CraftPlanCraft = CraftPlanLine & {
  recipe: number;
  crafts: number;
  /** crafts × yield (≥ qty; the rest is surplus). */
  produced: number;
};

export type CraftPlan = {
  /** Base materials to gather, largest first. */
  raw: CraftPlanLine[];
  /** Everything crafted on the way (targets included), in build order. */
  crafted: CraftPlanCraft[];
  /** Stations used, with the number of crafts at each. */
  stations: { station: CraftStation; crafts: number }[];
  /** Left over after crafting (rounding up to whole crafts, byproducts). */
  surplus: CraftPlanLine[];
  /** Items whose recipe loops back to an ancestor — counted as raw there. */
  cycles: string[];
  /** "Any of" slots in the plan: the member used and the total amount. */
  slots: CraftPlanSlot[];
  /** Taken from the player's stock instead of gathered / crafted. */
  fromStock: CraftPlanLine[];
};

/** Item id → amount the player already has (used before crafting). */
export type CraftStock = Record<string, number>;

export type CraftPlanSlot = { group: string; id: string; qty: number };

/**
 * The whole shopping list for `targets`, aggregated across the tree so a
 * shared intermediate is crafted in whole batches once (not rounded up per
 * branch). A recipe that leads back to an item already being expanded (a
 * cycle) stops there: that input is counted as raw. `stock` is used up
 * first at every item (102 Stone Brick in stock → only the rest is crafted,
 * and only their Stone gathered).
 */
export function planCrafting(
  graph: CraftingGraph,
  targets: CraftTarget[],
  choice: RecipeChoice = {},
  stock: CraftStock = {},
): CraftPlan {
  // 1. DFS over chosen recipes: post-order + back edges (cycles).
  const order: string[] = [];
  const state = new Map<string, 1 | 2>(); // 1 = on stack, 2 = done
  const backEdges = new Set<string>(); // `${from}\u0000${to}`
  const cycles = new Set<string>();
  const targetIds = new Set(targets.map((t) => t.id));
  const recipeOf = (id: string) =>
    chosenRecipe(graph, id, choice, targetIds.has(id));
  const visit = (root: string) => {
    if (state.has(root)) return;
    // Iterative DFS (deep factory chains must not blow the stack).
    const stack: { id: string; next: number; inputs: string[] }[] = [];
    const push = (id: string) => {
      state.set(id, 1);
      const ri = recipeOf(id);
      const inputs =
        ri === undefined
          ? []
          : graph.recipes[ri].ingredients.map((g) => ingredientId(g, choice));
      stack.push({ id, next: 0, inputs });
    };
    push(root);
    while (stack.length) {
      const top = stack[stack.length - 1];
      if (top.next < top.inputs.length) {
        const to = top.inputs[top.next++];
        const s = state.get(to);
        if (s === 1) {
          backEdges.add(`${top.id}\u0000${to}`);
          cycles.add(to);
        } else if (s === undefined) push(to);
      } else {
        state.set(top.id, 2);
        order.push(top.id);
        stack.pop();
      }
    }
  };
  for (const t of targets) visit(t.id);

  // 2. Push demand down in topological order (reverse post-order).
  const demand = new Map<string, number>();
  const raw = new Map<string, number>();
  const add = (m: Map<string, number>, id: string, n: number) =>
    m.set(id, (m.get(id) ?? 0) + n);
  for (const t of targets) if (t.qty > 0) add(demand, t.id, t.qty);

  const crafted: CraftPlanCraft[] = [];
  const surplus = new Map<string, number>();
  const stationCrafts = new Map<string, { station: CraftStation; n: number }>();
  const slotUse = new Map<string, CraftPlanSlot>();
  const used = new Map<string, number>();
  /** What is still needed of `n` × `id` after using the stock. */
  const afterStock = (id: string, n: number) => {
    const have = stock[id];
    if (!(have > 0)) return n;
    const take = Math.min(n, have - (used.get(id) ?? 0));
    if (take <= 0) return n;
    add(used, id, take);
    return n - take;
  };
  for (let k = order.length - 1; k >= 0; k--) {
    const id = order[k];
    const need = afterStock(id, demand.get(id) ?? 0);
    if (need <= 0) continue;
    const section = graph.sectionOf[id] ?? "";
    const ri = recipeOf(id);
    if (ri === undefined) {
      add(raw, id, need);
      continue;
    }
    const recipe = graph.recipes[ri];
    const y = recipeYield(recipe, id);
    const crafts = Math.ceil(need / y);
    crafted.push({
      id,
      section,
      qty: need,
      recipe: ri,
      crafts,
      produced: crafts * y,
    });
    if (crafts * y > need) add(surplus, id, crafts * y - need);
    for (const p of recipe.products) {
      if (p.id !== id) add(surplus, p.id, p.count * crafts);
    }
    for (const g of recipe.ingredients) {
      const n = g.count * crafts;
      const use = ingredientId(g, choice);
      if (g.any) {
        const prev = slotUse.get(g.any.group);
        if (prev) prev.qty += n;
        else slotUse.set(g.any.group, { group: g.any.group, id: use, qty: n });
      }
      if (backEdges.has(`${id}\u0000${use}`)) add(raw, use, afterStock(use, n));
      else add(demand, use, n);
    }
    for (const s of recipe.stations) {
      const key = s.id ?? `label:${s.label}`;
      const prev = stationCrafts.get(key);
      if (prev) prev.n += crafts;
      else stationCrafts.set(key, { station: s, n: crafts });
    }
  }

  const sectionFor = (id: string) => graph.sectionOf[id] ?? "";
  const lines = (m: Map<string, number>) =>
    [...m]
      .filter(([, n]) => n > 0)
      .map(([id, qty]) => ({ id, section: sectionFor(id), qty }))
      .sort((a, b) => b.qty - a.qty || a.id.localeCompare(b.id));
  return {
    raw: lines(raw),
    crafted,
    stations: [...stationCrafts.values()]
      .map(({ station, n }) => ({ station, crafts: n }))
      .sort((a, b) => b.crafts - a.crafts),
    surplus: lines(surplus),
    cycles: [...cycles],
    slots: [...slotUse.values()],
    fromStock: lines(used),
  };
}

/** One input of a flowchart step (`step` = the step that makes it, if any). */
export type CraftFlowInput = {
  id: string;
  qty: number;
  /** "Any of" slot the input fills. */
  group?: string;
  /** 1-based stage of the step that crafts it; undefined = gathered/bought. */
  stage?: number;
};

export type CraftFlowStep = CraftPlanCraft & {
  inputs: CraftFlowInput[];
  /**
   * Split of `inputs` for a craft that starts with some ingredients and is
   * finished with the rest (recipe `start`); undefined = all at once.
   */
  phases?: { start: CraftFlowInput[]; finish: CraftFlowInput[] };
};

export type CraftFlow = {
  /** Base materials to gather or buy (same totals as `planCrafting`). */
  raw: CraftPlanLine[];
  /**
   * Crafting stages, inputs first: every step of stage N only needs raw
   * materials and the outputs of earlier stages. The targets are last.
   */
  stages: CraftFlowStep[][];
  cycles: string[];
};

/**
 * The plan as a flowchart: `planCrafting`'s aggregated crafts, layered so
 * each stage only uses what earlier stages made — "gather, then craft from
 * top to bottom".
 */
export function craftFlow(
  graph: CraftingGraph,
  targets: CraftTarget[],
  choice: RecipeChoice = {},
): CraftFlow {
  const plan = planCrafting(graph, targets, choice);
  const stageOf = new Map<string, number>();
  const stages: CraftFlowStep[][] = [];
  // `plan.crafted` is in build order from the targets down: reversed, every
  // input's stage is known before its consumer (a cycle's back edge is not,
  // and counts as gathered — as in the plan).
  for (const c of [...plan.crafted].reverse()) {
    const recipe = graph.recipes[c.recipe];
    const start = new Set(recipe.start ?? []);
    const startInputs: CraftFlowInput[] = [];
    const finishInputs: CraftFlowInput[] = [];
    let stage = 1;
    const inputs = recipe.ingredients.map((g) => {
      const id = ingredientId(g, choice);
      const from = stageOf.get(id);
      if (from !== undefined) stage = Math.max(stage, from + 1);
      const input: CraftFlowInput = {
        id,
        qty: g.count * c.crafts,
        ...(g.any ? { group: g.any.group } : {}),
        ...(from !== undefined ? { stage: from } : {}),
      };
      const starts =
        start.has(g.id) || !!g.any?.options.some((o) => start.has(o.id));
      (starts ? startInputs : finishInputs).push(input);
      return input;
    });
    stageOf.set(c.id, stage);
    (stages[stage - 1] ??= []).push({
      ...c,
      inputs,
      ...(startInputs.length && finishInputs.length
        ? { phases: { start: startInputs, finish: finishInputs } }
        : {}),
    });
  }
  return {
    raw: plan.raw,
    stages,
    cycles: plan.cycles,
  };
}

/** Longest ingredient chain under `id` (0 = raw), cycle-safe. */
export function craftDepth(
  graph: CraftingGraph,
  id: string,
  choice: RecipeChoice = {},
  seen: Set<string> = new Set(),
): number {
  const ri = chosenRecipe(graph, id, choice, seen.size === 0);
  if (ri === undefined || seen.has(id)) return 0;
  seen.add(id);
  let max = 0;
  for (const g of graph.recipes[ri].ingredients) {
    max = Math.max(
      max,
      craftDepth(graph, ingredientId(g, choice), choice, seen),
    );
  }
  seen.delete(id);
  return max + 1;
}

const MAX_QTY = 1_000_000;

/** `?items=id:qty,id2:qty` → targets (bad parts dropped, qty clamped). */
export function parseCraftItems(
  param: string | null | undefined,
): CraftTarget[] {
  if (!param) return [];
  const out: CraftTarget[] = [];
  for (const part of param.split(",")) {
    if (!part) continue;
    const at = part.lastIndexOf(":");
    if (at === 0) continue;
    const rawId = at > 0 ? part.slice(0, at) : part;
    const n = at > 0 ? Number(part.slice(at + 1)) : 1;
    let id: string;
    try {
      id = decodeURIComponent(rawId);
    } catch {
      continue;
    }
    if (!id) continue;
    const qty = Number.isFinite(n)
      ? Math.min(MAX_QTY, Math.max(1, Math.floor(n)))
      : 1;
    const prev = out.find((t) => t.id === id);
    if (prev) prev.qty = Math.min(MAX_QTY, prev.qty + qty);
    else out.push({ id, qty });
  }
  return out;
}

/** Inverse of `parseCraftItems`. */
export function formatCraftItems(targets: CraftTarget[]): string {
  return targets.map((t) => `${encodeURIComponent(t.id)}:${t.qty}`).join(",");
}

/** `?r=item:recipeKey,...` → choice (keys must exist for that item). */
export function parseRecipeChoice(
  param: string | null | undefined,
  graph?: CraftingGraph,
): RecipeChoice {
  const out: RecipeChoice = {};
  if (!param) return out;
  for (const part of param.split(",")) {
    const at = part.indexOf(":");
    if (at <= 0) continue;
    let id: string, key: string;
    try {
      id = decodeURIComponent(part.slice(0, at));
      key = decodeURIComponent(part.slice(at + 1));
    } catch {
      continue;
    }
    if (graph) {
      const slot = id.startsWith("~") ? graph.slots[id.slice(1)] : undefined;
      const ok = slot
        ? slot.includes(key)
        : (graph.byProduct[id] ?? []).some((i) => graph.recipes[i].key === key);
      if (!ok) continue;
    }
    out[id] = key;
  }
  return out;
}

/** Inverse of `parseRecipeChoice`; drops choices equal to the default. */
export function formatRecipeChoice(
  choice: RecipeChoice,
  graph?: CraftingGraph,
): string {
  return Object.entries(choice)
    .filter(([id, key]) => {
      if (!graph) return true;
      if (id.startsWith("~")) return graph.slots[id.slice(1)]?.[0] !== key;
      return graph.recipes[graph.byProduct[id]?.[0] ?? -1]?.key !== key;
    })
    .map(([id, key]) => `${encodeURIComponent(id)}:${encodeURIComponent(key)}`)
    .join(",");
}
